/// <reference lib="dom" />
import { errorMessage } from '../services/apiError';
import { CreateOrderResponse, paymentService } from '../services/paymentService';
import {
  RazorpayInstance,
  RazorpayOptions,
  RazorpayOrderOptions,
  RazorpayResponse,
} from '../types/razorpay';
import { trackVerifiedPurchase } from '../utils/analytics';



type PaymentHandlerParams = {
  orderData: CreateOrderResponse;
  onSuccess?: (response: { paymentId: string; credits: number }) => void;
  onFailure?: (error: string) => void;
  setError: (e: string | null) => void;
  setIsProcessing: (p: boolean) => void;
};

const verifyRazorpayPayment = async (
  response: RazorpayResponse,
  params: PaymentHandlerParams
): Promise<void> => {
  if (!response.razorpay_order_id) {
    throw new Error('Missing razorpay_order_id');
  }

  const verifyResult = await paymentService.verifyPayment(
    response.razorpay_order_id,
    response.razorpay_payment_id,
    response.razorpay_signature
  );

  trackVerifiedPurchase(verifyResult.purchase);

  if (params.onSuccess) {
    params.onSuccess({
      paymentId: verifyResult.payment_id,
      credits: verifyResult.prompt_credits || params.orderData.prompt_credits,
    });
  }
};

const handlePaymentError = (
  verifyError: unknown,
  params: Pick<PaymentHandlerParams, 'onFailure' | 'setError'>
): string => {
  const errMsg = verifyError instanceof Error ? verifyError.message : 'Payment verification failed';

  params.setError(errMsg);
  if (params.onFailure) params.onFailure(errMsg);

  return errMsg;
};

const createPaymentHandler =
  (params: PaymentHandlerParams) => async (response: RazorpayResponse): Promise<void> => {
    try {
      await verifyRazorpayPayment(response, params);
    } catch (verifyError) {
      handlePaymentError(verifyError, params);
    } finally {
      params.setIsProcessing(false);
    }
  };

const buildRazorpayOptions = (
  orderData: CreateOrderResponse,
  handler: (response: RazorpayResponse) => void,
  prefill: { name?: string; email?: string } | undefined,
  onDismiss: (() => void) | undefined,
  setIsProcessing: (p: boolean) => void
): RazorpayOrderOptions => ({
  key: orderData.key_id,
  amount: orderData.amount,
  currency: orderData.currency,
  name: 'MostlyAgent',
  description: `Payment for ${orderData.prompt_credits} prompt credits`,
  order_id: orderData.order_id,
  handler,
  prefill: { name: prefill?.name || '', email: prefill?.email || '' },
  theme: { color: '#6366f1' },
  modal: {
    ondismiss: () => {
      setIsProcessing(false);
      if (onDismiss) onDismiss();
    },
  },
  ...(orderData.currency !== 'INR' ? { method: { upi: false, netbanking: false, wallet: false, card: true } } : {}),
});

const handleOrderCreationError = (
  createError: unknown,
  setError: (e: string | null) => void,
  setIsProcessing: (p: boolean) => void,
  onFailure?: (error: string) => void
): void => {
  const errMsg = errorMessage(createError, 'We couldn’t start the payment. Check your payment details and try again.');

  setError(errMsg);
  setIsProcessing(false);
  if (onFailure) onFailure(errMsg);
};

const notifyScriptNotLoaded = (
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): void => {
  const err = 'The payment service is still loading. Wait a moment and try again.';

  setError(err);
  if (onFailure) onFailure(err);
};

const checkScriptLoaded = (
  isScriptLoaded: boolean,
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): boolean => {
  if (!isScriptLoaded) {
    notifyScriptNotLoaded(setError, onFailure);

    return false;
  }

  return true;
};

const handleScriptError = (
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): void => {
  const errMsg = 'We couldn’t load the payment service. Check your connection and try again.';

  setError(errMsg);
  if (onFailure) onFailure(errMsg);
};

type ScriptElement = globalThis.HTMLScriptElement;

const configureScriptElement = (
  script: ScriptElement,
  scriptUrl: string,
  setIsScriptLoaded: (loaded: boolean) => void,
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): void => {
  script.src = scriptUrl;
  script.async = true;
  script.onload = () => setIsScriptLoaded(true);
  script.onerror = () => handleScriptError(setError, onFailure);
};

const createScriptElement = (
  scriptUrl: string,
  setIsScriptLoaded: (loaded: boolean) => void,
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): ScriptElement => {
  const script = document.createElement('script');

  configureScriptElement(script, scriptUrl, setIsScriptLoaded, setError, onFailure);

  return script;
};

export const loadRazorpayScript = (
  scriptUrl: string,
  setIsScriptLoaded: (loaded: boolean) => void,
  setError: (e: string | null) => void,
  onFailure?: (error: string) => void
): void => {
  const script = createScriptElement(scriptUrl, setIsScriptLoaded, setError, onFailure);

  document.body.appendChild(script);
};

const openRazorpayCheckout = (
  razorpayClass: new (options: RazorpayOptions) => RazorpayInstance,
  orderData: CreateOrderResponse,
  onSuccess: ((response: { paymentId: string; credits: number }) => void) | undefined,
  onFailure: ((error: string) => void) | undefined,
  prefill: { name?: string; email?: string } | undefined,
  onDismiss: (() => void) | undefined,
  setError: (e: string | null) => void,
  setIsProcessing: (p: boolean) => void
): void => {
  const handler = createPaymentHandler({ orderData, onSuccess, onFailure, setError, setIsProcessing });
  const razorpayOptions = buildRazorpayOptions(orderData, handler, prefill, onDismiss, setIsProcessing);
  const razorpay = new razorpayClass(razorpayOptions);

  razorpay.open();
};

const processPaymentCheckout = async (
  razorpayClass: new (options: RazorpayOptions) => RazorpayInstance,
  taskIds: string[] | undefined,
  onSuccess: ((response: { paymentId: string; credits: number }) => void) | undefined,
  onFailure: ((error: string) => void) | undefined,
  prefill: { name?: string; email?: string } | undefined,
  onDismiss: (() => void) | undefined,
  setError: (e: string | null) => void,
  setIsProcessing: (p: boolean) => void
): Promise<void> => {
  const orderData: CreateOrderResponse = await paymentService.createOrder(taskIds);

  openRazorpayCheckout(razorpayClass, orderData, onSuccess, onFailure, prefill, onDismiss, setError, setIsProcessing);
};

const initPaymentState = (
  setIsProcessing: (p: boolean) => void,
  setError: (e: string | null) => void
): void => {
  setIsProcessing(true);
  setError(null);
};

export const initiateRazorpayPayment = async (
  isScriptLoaded: boolean,
  setIsProcessing: (p: boolean) => void,
  setError: (e: string | null) => void,
  razorpayClass: new (options: RazorpayOptions) => RazorpayInstance,
  taskIds: string[] | undefined,
  onSuccess: ((response: { paymentId: string; credits: number }) => void) | undefined,
  onFailure: ((error: string) => void) | undefined,
  prefill: { name?: string; email?: string } | undefined,
  onDismiss: (() => void) | undefined
): Promise<void> => {
  if (!checkScriptLoaded(isScriptLoaded, setError, onFailure)) return;

  initPaymentState(setIsProcessing, setError);

  try {
    await processPaymentCheckout(
      razorpayClass, taskIds, onSuccess, onFailure, prefill, onDismiss, setError, setIsProcessing
    );
  } catch (createError) {
    handleOrderCreationError(createError, setError, setIsProcessing, onFailure);
  }
};

const processTopUpCheckout = async (
  razorpayClass: new (options: RazorpayOptions) => RazorpayInstance,
  creditAmount: number,
  currency: string,
  onSuccess: ((response: { paymentId: string; credits: number }) => void) | undefined,
  onFailure: ((error: string) => void) | undefined,
  prefill: { name?: string; email?: string } | undefined,
  onDismiss: (() => void) | undefined,
  setError: (e: string | null) => void,
  setIsProcessing: (p: boolean) => void
): Promise<void> => {
  const orderData = await paymentService.createTopUpOrder(creditAmount, currency);

  // Handle Stripe provider: redirect to Stripe Checkout
  if (orderData.payment_provider === 'stripe' && orderData.stripe_checkout_url) {
    window.location.href = orderData.stripe_checkout_url;

    return;
  }

  const compatibleOrderData = {
    ...orderData,
    task_count: 0,
  } as CreateOrderResponse;

  openRazorpayCheckout(
    razorpayClass,
    compatibleOrderData,
    onSuccess,
    onFailure,
    prefill,
    onDismiss,
    setError,
    setIsProcessing
  );
};

export const initiateRazorpayTopUp = async (
  isScriptLoaded: boolean,
  setIsProcessing: (p: boolean) => void,
  setError: (e: string | null) => void,
  razorpayClass: new (options: RazorpayOptions) => RazorpayInstance,
  creditAmount: number,
  currency: string = 'USD',
  onSuccess: ((response: { paymentId: string; credits: number }) => void) | undefined,
  onFailure: ((error: string) => void) | undefined,
  prefill: { name?: string; email?: string } | undefined,
  onDismiss: (() => void) | undefined
): Promise<void> => {
  if (!checkScriptLoaded(isScriptLoaded, setError, onFailure)) return;

  initPaymentState(setIsProcessing, setError);

  try {
    await processTopUpCheckout(
      razorpayClass, creditAmount, currency, onSuccess, onFailure, prefill, onDismiss, setError, setIsProcessing
    );
  } catch (createError) {
    handleOrderCreationError(createError, setError, setIsProcessing, onFailure);
  }
};
